import {
	checkCustomerExists,
	createCustomer,
	getLocation,
	goToPayment,
	createOrder,
} from "@/utils/apiFunctions";
import { mapGetters } from "vuex";
import toast from "./toast";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { NativeSettings } from "capacitor-native-settings";

export default {
	mixins: [toast],
	name: "Payment",
	data() {
		return {
			paymentButton: "",
			customerData: null,
			orderID: null,
		};
	},
	mounted() {
		window.addEventListener("beforeunload", this.handleBeforeUnload);
		window.addEventListener("message", this.handleMessage);
		this.getPaymentFailure();
	},
	beforeDestroy() {
		window.removeEventListener("message", this.handleMessage);
		window.removeEventListener("beforeunload", this.handleBeforeUnload);
	},
	watch: {
		paymentButton(value) {
			if (value) {
				const button = this.$refs.responseContainer.querySelector("button");
				if (button) {
					button.click();
				}
			}
		},
	},
	computed: {
		...mapGetters("candidateVUEX", {
			primaryCandidateInfo: "getPrimaryCandidateInfo",
		}),
		...mapGetters("userVUEX", {
			userInfo: "getUserInfo",
		}),
	},
	methods: {
		executeScriptTags() {
			const scripts = this.$refs.responseContainer.querySelectorAll("script");
			scripts.forEach((script) => {
				const scriptContent = script.textContent || script.innerText;
				const dynamicScript = document.createElement("script");
				dynamicScript.textContent = scriptContent;
				document.body.appendChild(dynamicScript);
				document.body.removeChild(dynamicScript);
			});
		},
		async paymentButtonShow(orderID) {
			try {
				const resp = await goToPayment(orderID);
				if (resp.status == 200) {
					this.$nextTick(() => {
						this.$refs.responseContainer.innerHTML = resp.data;
						this.paymentButton = resp.data;
						this.executeScriptTags();
					});
				} else {
					this.loaderStatus = false;
					this.errorModal("Error", "Something went wrong !");
				}
			} catch (error) {
				console.log(error);
				this.loaderStatus = false;
				this.errorModal("Error", "Something went wrong !");
			}
		},
		loadRazorpayLibrary(orderID) {
			const script = document.createElement("script");
			script.src = "https://checkout.razorpay.com/v1/checkout.js";
			script.onload = () => {
				this.paymentButtonShow(orderID);
			};
			document.head.appendChild(script);
		},
		async getLocationDetails() {
			if (Capacitor.isNativePlatform()) {
				try {
					let permissionStatus = await Geolocation.checkPermissions();
					if (permissionStatus.location !== "granted") {
						permissionStatus = await Geolocation.requestPermissions();
						if (permissionStatus.location !== "granted") {
							this.loaderStatus = false;
							this.errorModal(
								"Error",
								"Please enable location permission to continue!"
							);
							return;
						}
					}

					// If native Google Play Services prompt bridge is available, invoke it
					if (
						window.ShareCopyBridge &&
						typeof window.ShareCopyBridge.enableGPS === "function"
					) {
						window.ShareCopyBridge.enableGPS();
					}

					const position = await Geolocation.getCurrentPosition({
						enableHighAccuracy: true,
						timeout: 10000,
						maximumAge: 5000,
					});

					const latitude = position?.coords?.latitude;
					const longitude = position?.coords?.longitude;
					if (latitude && longitude) {
						this.getUserLocation(latitude, longitude);
					}
				} catch (error) {
					this.loaderStatus = false;
					console.error("Native geolocation error:", error);

					const isLocationDisabled =
						error?.message?.includes("Location services are not enabled") ||
						error?.message?.includes("location unavailable") ||
						error?.code === 2;

					if (isLocationDisabled) {
						if (
							window.ShareCopyBridge &&
							typeof window.ShareCopyBridge.enableGPS === "function"
						) {
							window.ShareCopyBridge.enableGPS();
						} else {
							// Open device location settings so user can toggle GPS on
							try {
								if (Capacitor.getPlatform() === "android") {
									try {
										await NativeSettings.openAndroid({ option: "location" });
									} catch (e) {
										await NativeSettings.open({
											optionAndroid: "location",
											optionIOS: "app",
										});
									}
								} else if (Capacitor.getPlatform() === "ios") {
									await NativeSettings.openIOS({ option: "app" });
								}
							} catch (e) {
								console.error("Failed to open location settings:", e);
							}
						}

						this.errorModal(
							"Error",
							"Please enable location permission to continue!"
						);
					} else {
						this.errorModal(
							"Error",
							"Please enable location permission to continue!"
						);
					}
				}
				return;
			}

			// Web browser fallback
			if ("geolocation" in navigator) {
				navigator.geolocation.getCurrentPosition(
					(position) => {
						const latitude = position.coords.latitude;
						const longitude = position.coords.longitude;
						if (latitude && longitude) {
							this.getUserLocation(latitude, longitude);
						}
					},
					(error) => {
						this.loaderStatus = false;
						this.errorModal(
							"Error",
							"Please enable location permission to continue!"
						);
					}
				);
			} else {
				this.loaderStatus = false;
				this.errorModal(
					"Error",
					"Geolocation is not supported by this browser."
				);
			}
		},
		async checkCustomer() {
			try {
				const response = await checkCustomerExists(this.userInfo.email);
				if (response.status === 200) {
					this.customerData = response.data.data.customer;
					this.purchaseCourse();
				} else {
					// this.createNewCustomer("Bangalore, Karnataka");
					this.getLocationDetails();
				}
			} catch (error) {
				console.log(error);
				this.getLocationDetails();
			}
		},
		async getUserLocation(latitude, longitude) {
			try {
				let payload = {
					latitude: latitude.toString(),
					longitude: longitude.toString(),
				};
				const response = await getLocation(payload);
				if (response.status == 200) {
					this.createNewCustomer(response.data.data.formatted_address);
				} else {
					this.loaderStatus = false;
					this.errorModal("Error", "Something went wrong !");
				}
			} catch (error) {
				this.loaderStatus = false;
				this.errorModal("Error", "Something went wrong !");
			}
		},
		async createNewCustomer(address) {
			try {
				let payload = {
					fullName: this.primaryCandidateInfo.fullName,
					customerAddress: address,
					email: this.primaryCandidateInfo.email,
					phoneNumber: this.primaryCandidateInfo.phoneNumber,
				};
				const response = await createCustomer(payload);
				if (response.status == 200) {
					this.customerData = response.data.data.customer;
					this.purchaseCourse();
				} else {
					this.loaderStatus = false;
					this.errorModal("Error", "Something went wrong !");
				}
			} catch (error) {
				console.log(error);
				this.loaderStatus = false;
				this.errorModal("Error", "Something went wrong !");
			}
		},
		async createOrder() {
			try {
				const payload = {
					customerID: this.customerData.customerID,
					email: this.userInfo.email,
					items: [{ itemID: this.paymentItemID, quantity: 1 }],
					currency: "INR",
					platform: "REC_CANDIDATE",
				};
				const response = await createOrder(payload);
				if (response.status === 201) {
					this.orderID = response.data.data.data.orderID;
					return response.data.data.data;
				} else {
					this.loaderStatus = false;
					this.errorModal(response.data.data.data);
					return null;
				}
			} catch (error) {
				console.log(error);
				return null;
			}
		},
		async purchaseCourse() {
			try {
				this.paymentProcessing = true;

				const { orderID } = await this.createOrder();
				this.orderID = orderID;
				if (orderID) {
					this.loadRazorpayLibrary(orderID);
				}
			} catch (error) {
				console.log(error);
			}
		},
		async createTransaction(order_id, isFree = false) {
			try {
				const response = await createTransaction({
					order_id,
					isFree,
					platform: "REC_CANDIDATE",
				});
				if (response.status === 201) {
					this.postPurchaseTest(true);
				} else {
					this.loaderStatus = false;
					this.errorModal(
						"Error",
						"Something went wrong while creating transaction!"
					);
				}
			} catch (error) {
				console.log(error);
			} finally {
				this.loaderStatus = false;
				this.paymentProcessing = false;
			}
		},
		handleBeforeUnload(event) {
			if (this.paymentProcessing) {
				event.preventDefault(); // This is necessary to display the alert message
			}
		},
		handleMessage(event) {
			if (event.data?.source === "hire3x-payment") {
				if (event.data.data.type == "Success") {
					this.paymentProcessing = false;
					this.postPurchaseTest(true);
				}
			}
		},
		getPaymentFailure() {
			const originalLog = console.log;
			console.log = (...args) => {
				// Call the original console.log method
				originalLog.apply(console, args);
				// Check if the logged message contains the data you're interested in
				const logMessage = args.join(" ");
				if (
					logMessage.includes("payment cancelled") &&
					this.paymentProcessing
				) {
					this.loaderStatus = false;
					this.errorModal("Error", "Payment cancelled!");
					this.paymentProcessing = false;
					this.enrollButtonLoader = false;
					this.$refs.testDetails.loaderStatus = false;
				}
			};
		},
	},
};
